// @givmo/board — EVERY word the annual conflict-of-interest disclosures add, in one place: the
// officer's "Annual disclosures" part and the register grouped by person (CBG-YR2-WEB1), and the
// person's own year (CBG-YR2-WEB2).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the CBG-YR2 frames for building on 2026-10-12 15:56 PDT ("yes")
// with every word kept a draft, and then the words themselves in copy round CBG-YR2-COPY1 on
// 2026-10-13 09:27 PDT ("approved with your recs"), with the page's recommendations: the close
// dialog's late-answer line says until when, the officer's paper form keeps the person's statement
// as printed, and the mails greet the whole name. The lines no copy page showed went on their own
// page in copy round CBG-YR2-COPY2, approved 2026-10-13 14:48 PDT ("4)ok") with its suggestions: the
// minute book's unnamed closer reads "someone whose name isn’t shown", set off by a comma before
// the day. Each line names the frame that draws it (`o…` the officer's, `d…` the person's), or "not
// drawn" for a state no frame shows.
//
// And three from CBG-YR2-WEB3's frames (`y1`–`y4`, approved for building 2026-10-13 22:40 PDT,
// every word a draft), which keep the latest closed year's table and cite each register entry's
// record, are approved as drafted on their copy page
// (`scratchpad/2026-10-13-frames1-copy-round/copy-review.html`, 2026-10-14 08:26 PDT, "approved if
// you agree with it"). Each says so where it is.
//
// And CBG-FIX104's (2026-10-17 17:42 PDT, "1 and 2, approved.",
// `scratchpad/2026-10-17-fix104-copy-round/copy-review.html`): the year asks only the people
// serving on the board, and the roster now holds committee members who aren't on it, so the page's
// lead and the year's "your roster" lines say whom they mean. Each says so where it is.
//
// And CBG-UX-COPY2's (D-CBG-42, Jonathan, 2026-10-24 19:08 PDT: "conflicts of interest form instead
// of questionnaire everywhere"; its amendment of 21:54 PDT: one name for the yearly form everywhere,
// "Yes"): every line that named the questionnaire or the annual disclosure names the "conflicts of
// interest form", and the year's set the "conflicts of interest forms", with "fill out" for the
// verb. On this page, where the title already says "Conflicts of interest", a tab, heading or button
// takes the short form, "Yearly forms" and "the {label} forms" (the orchestrator's note, 22:33 PDT).
// "Disclosure" stays for a single disclosure.
//
// And CBG-UX-COPY3's (D-CBG-45, Jonathan, 2026-10-29 08:53 PDT; D-CBG-42 as amended 08:36 PDT,
// "Form"): one verb for the person's act, "fill out", with "sign" for its last step and "send" only
// for what officers do (D12); the record a person's yearly form makes is their "form" wherever a
// person reads it, "disclosure" kept for disclosing one interest (D13); every comparison names last
// year's form, and the form's line names the year it asks about (D11); a late joiner is told there's
// no due date (D8); "Whose:" labels an entry's answer as "Position:" does (D10); the reminder mail
// says it's a reminder (M2); the lead names the year its button starts (R1); "Disclosed outside the
// yearly form" (R2). Each mail preview changes with its template, so it still shows what is sent.
//
// The sentences the BACKEND serves (the questions, their sources and "What counts" lines, the
// template's review line and provenance, the signing sentence, the refusals) are not here: they are
// shown verbatim from the wire, and their words are the backend's (its contract §20).
//
// ⚠ Plan §3.2 and §3.4 bind every line: what was asked, who answered and when — never what anyone's
// interests mean, whether they should take part in a vote, or whether anything is "required",
// "overdue" or "late". The one count is of SUBMISSIONS; nothing counts conflicts. No pronoun for a
// named person (Board can't know them).

import { houseFormName } from '../../lib/house-form';

/** "Alice", "Alice and Ben", "Alice, Ben and Cy": names as a sentence lists them. */
export function nameList(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "1 person" · "2 people". */
function people(count: number): string {
  return count === 1 ? '1 person' : `${count} people`;
}

export const coiWords = {
  // ── The page ───────────────────────────────────────────────────────────────────────────────────

  // Drawn, every officer frame (v0 said "your directors"; D-CBG-21's roster
  // includes officers who aren't directors). Shown only where the year's disclosures are served.
  // CBG-FIX104 (row 3.1): the register also holds what an officer records for a committee member
  // who isn't on the board.
  pageDescription: 'What people have told your board about their interests, and when.',
  // Drawn, every officer frame (the two parts).
  registerTab: 'Register',
  annualTab: 'Yearly forms',

  // ── Before a year is started (o1-before) ────────────────────────────────────────────────────────

  // Drawn, o1-before. CBG-FIX104 (row 3.3): the year asks the people serving on the board; the
  // form named, D-CBG-42's ruled name (CBG-UX-COPY2's fold, F4); the year the button below it starts
  // (CBG-UX-COPY3's R1), so the two never disagree.
  beforeLead: (label: string) =>
    `Ask everyone serving on the board to fill out the ${label} conflicts of interest form.`,
  // Behind "How this works", collapsed in o1-before. The mockup's second line
  // read "…sees those answers again, to confirm or change", which Jonathan's answer to question 4 (no
  // tap per answer) retired; the builder's words, for the copy round. The first, CBG-FIX104 (3.4).
  beforeHow: [
    'Everyone serving on the board gets an email and a short conflicts of interest form in Board, directors or not.',
    'Someone who answered last year finds those answers filled in, and changes only what’s different.',
    'Each person’s signed form goes into your minute book.',
  ],
  // Drawn, o1-before and o2-start.
  startYear: (label: string) => `Start the ${label} forms`,
  // Not drawn — a reader who can't start one, with no year yet.
  noYearYet: 'No year’s conflicts of interest forms have been started here yet.',
  // Drawn, o1-before and o3-dashboard.
  earlierYears: 'Earlier years',
  yearTitle: (label: string) => `${label} forms`,
  // Drawn, o1-before ("Closed Feb 3, 2026 by Maya Solberg · 5 of 5 filled out").
  closedLine: (on: string, by: string | null, submitted: number, total: number) =>
    `Closed ${on}${by ? ` by ${by}` : ''} · ${submitted} of ${total} filled out`,
  // Not drawn — a year withdrawn before anyone answered.
  withdrawnLine: (on: string) => `Withdrawn ${on} · nobody had answered`,
  // Drawn, o1-before (the closing record's link).
  inMinuteBook: 'In your minute book',

  // ── Starting a year (o2-start, o2b-start-questions) ─────────────────────────────────────────────

  // Drawn, o2-start. CBG-FIX104 (row 3.5).
  startLead:
    'Everyone serving on the board gets an email asking for their answers, and a conflicts of interest form in Board.',
  yearField: 'Year',
  dueField: 'Answers due',
  periodField: 'The questions ask about',
  // Drawn, o2-start ("2026 (January 1 to December 31)").
  periodYear: (year: number) => `${year} (January 1 to December 31)`,
  // Drawn, o2-start (the select's second choice; its two fields not drawn).
  periodOther: 'Other dates',
  periodStartField: 'From',
  periodEndField: 'To',
  // Drawn, o2-start and o2b-start-questions.
  questionsLabel: 'The questions',
  starting: 'Givmo’s starting conflicts of interest form',
  // Drawn, o2-start ("6 questions about interests, and one statement about
  // your policy."); the statement's half only while a policy is shown.
  questionsSummary: (interests: number, withStatement: boolean) =>
    `${interests} ${interests === 1 ? 'question' : 'questions'} about interests${
      withStatement ? ', and one statement about your policy' : ''
    }. Turn off any you don’t want to ask this year.`,
  seeQuestions: 'See the questions',
  hideQuestions: 'Hide the questions',
  // Drawn, o2b-start-questions (each question's source line, and the sixth's).
  from: (source: string) => `From: ${source}`,
  directorsOnly: 'Directors only',
  // Not drawn — a switch's accessible name.
  askQuestion: (number: number) => `Ask question ${number}`,
  // Not drawn — the statement switched off for want of a policy.
  noPolicyQuestion: 'Asked only when your board has a conflict-of-interest policy in Governance.',
  // Drawn, o2-start.
  policyLabel: 'Your policy',
  // Drawn, o2-start ("Adopted Mar 12, 2025 · from Governance"). Not drawn: "From Governance",
  // for a policy recorded with no adoption date.
  policyAdoptedFromGovernance: (on: string | null) =>
    on === null ? 'From Governance' : `Adopted ${on} · from Governance`,
  // Drawn, o2-start.
  policyNote: 'Everyone is shown it to read, and asked to confirm they’ve received and read it.',
  // Not drawn — no policy in Governance (the derivation's one factual line;
  // Board never says a policy is needed).
  noPolicy:
    'No conflict-of-interest policy is recorded in Governance, so the questions ask about interests only.',
  // Drawn, o2-start.
  whosAsked: 'Who’s asked',
  // Drawn, o2-start ("Everyone on your roster, 6 people: …"). CBG-FIX104 (row 3.6): the names that
  // follow are the board's people only.
  everyoneOnRoster: (names: readonly string[]) =>
    `Everyone serving on the board, ${people(names.length)}: ${nameList(names)}.`,
  // Drawn, o2-start (one person). Not drawn: the plural.
  notJoined: (names: readonly string[]) =>
    names.length === 1
      ? `${names[0]} hasn’t joined Board yet: that email includes an invitation to join.`
      : `${nameList(names)} haven’t joined Board yet: their emails include an invitation to join.`,
  // Not drawn — a seat with no email is asked on paper.
  noEmail: (names: readonly string[]) =>
    names.length === 1
      ? `${names[0]} has no email on the roster: you can record a paper form for them.`
      : `${nameList(names)} have no email on the roster: you can record a paper form for each.`,
  // Not drawn — nobody on the roster yet. CBG-FIX104 (row 3.7): a roster holding only committee
  // members who aren't on the board isn't empty, and the year asks none of them.
  nobodyOnRoster: 'There’s nobody serving on the board yet. Add people under Board members first.',
  // Drawn, o2-start.
  theEmail: 'The email',
  // Drawn, o2-start ("Start and send to 6 people").
  startAndSend: (count: number) => `Start and send to ${people(count)}`,
  cancel: 'Cancel',
  // Not drawn — the start went out.
  started: (label: string, sent: number) =>
    `The ${label} conflicts of interest forms are open. ${sent === 1 ? '1 email' : `${sent} emails`} sent.`,

  // ── The email, as it will be sent (o2-start, o4-remind) ─────────────────────────────────────────
  // Mirrors the backend's own templates (`board_coi_disclosure_request_email.html`,
  // `…_reminder_email.html`), approved there too: the preview shows what is sent, and the sender's
  // address is the one every Board mail goes from.

  // Drawn, o2-start and o4-remind.
  emailFrom: (organization: string) => `From ${organization} Board <no-reply@givmocharitable.org>`,
  emailSubject: (label: string) => `Your ${label} conflicts of interest form`,
  // CBG-UX-COPY3's M2: the reminder's subject and opening, as the backend's reminder template sends
  // them; the request keeps `emailSubject` and `emailAsk`.
  emailReminderSubject: (label: string) => `Reminder: your ${label} conflicts of interest form`,
  // The backend greets with the person's whole name (the copy round's question; the frames drew a
  // first name).
  emailHello: (name: string) => `Hello ${name},`,
  emailAsk: (organization: string) =>
    `${organization} asks everyone on its board to fill out a yearly conflicts of interest form.`,
  emailReminderAsk: (organization: string) =>
    `A reminder: ${organization} asks everyone on its board to fill out a yearly conflicts of interest form.`,
  emailPleaseSendBy: (due: string) => `Please fill yours out by ${due}.`,
  // CBG-YR4-COI-JOINER (approved, CBG-YR4-COPY1's first part): the backend mail's line for a member
  // who joined after the year closed, in place of the due date (board-coi-cycle.md §22.9).
  emailJoinedAfterClose: (label: string) =>
    `Its ${label} forms were collected before you joined, so it’s asking you for yours now. There’s no due date.`,
  emailYoursDue: (due: string) => `Yours is due ${due}.`,
  emailWasDue: (due: string) => `It was due ${due}. You can still fill it out.`,
  // CBG-FIX143: the mail's house form, "the {organization} board" (the backend template's own),
  // a name's own leading "The" dropped as the mail drops it (`houseFormName`).
  emailNotJoined: (organization: string) =>
    `You’ll join the ${houseFormName(organization)} board in Givmo with your own account first.`,
  emailButton: 'Open your form',

  // ── The open year (o3-dashboard) ────────────────────────────────────────────────────────────────

  // Drawn, o3-dashboard ("Due Jan 29 · the questions ask about 2026 · opened
  // Jan 5 by Maya Solberg.").
  yearFacts: (due: string, period: string, opened: string, by: string | null) =>
    `Due ${due} · the questions ask about ${period} · opened ${opened}${by ? ` by ${by}` : ''}.`,
  // Behind the year's "How this works" (the mockups' word table). The first, CBG-FIX104 (row 3.8):
  // a committee member who isn't on the board is neither asked nor added; one added to the board is
  // added then.
  yearHow: [
    'Everyone serving on the board when you started is asked, directors or not. Someone who joins the board later is added here for you to send.',
    'Each person’s signed form goes into your minute book, in place of their form from last year.',
    'Reminders go only when you send them, at most once a day to each person.',
    'Closing the year adds one record to your minute book: who was asked, and who filled it out and when. It names no one’s interests.',
  ],
  // Drawn, o3-dashboard ("Remind 2 people").
  remindPeople: (count: number) => `Remind ${people(count)}`,
  // Not drawn — the year's menu's accessible name.
  yearMenu: (label: string) => `More for the ${label} forms`,
  // Drawn, o3-dashboard (the year's menu).
  downloadAll: 'Download every response (PDF)',
  changeDue: 'Change the due date',
  closeYear: (label: string) => `Close the ${label} forms`,
  // Not drawn — offered only before anyone has answered.
  withdrawYear: (label: string) => `Withdraw the ${label} forms`,
  // Drawn, o3-dashboard ("4 of 7 filled out"; the figures bold).
  submittedOf: (submitted: number, total: number) => `${submitted} of ${total}`,
  submittedWord: 'filled out',
  // Drawn, o3-dashboard (the table's columns). Not drawn: "Changes" with no earlier year to name,
  // and the actions column's "Actions".
  personColumn: 'Person',
  statusColumn: 'Status',
  changesColumn: (label: string | null) =>
    label === null ? 'Changes' : `Changes from the ${label} form`,
  actionsColumn: 'Actions',
  // Drawn, o3-dashboard (the statuses and their notes).
  statusSubmitted: (on: string) => `Filled out ${on}`,
  onPaper: 'On a paper form',
  // Not drawn — the derivation's "Filled out Jan 12 · corrected Jan 14".
  correctedOn: (on: string) => `Corrected ${on}`,
  // Not drawn — a first answer after the close (Q7).
  receivedAfterClose: 'Received after the year closed',
  statusNotSubmitted: 'Not filled out yet',
  hasntJoined: 'Hasn’t joined Board yet',
  remindedOn: (on: string) => `Reminded ${on}`,
  // Not drawn — a seat with no email on the roster.
  noEmailOnRoster: 'No email on the roster',
  statusNotSent: 'Not sent yet',
  // Approved (CBG-YR2-WEB3's copy page, 2026-10-14), drawn y1: in a closed year, someone the
  // year's request never reached. A joiner the year never went to (`is_recipient: false`), which
  // the backend can't send it now (contract §5.3), is quiet and offered nothing. A recipient whose
  // request was held back or failed (`is_recipient: true`) keeps the paper acts, since the backend
  // takes its late answer (`offersPaperForm`; review-134's F1).
  statusNotSentClosed: 'Not sent',
  joinedBoardOn: (on: string) => `Joined the board ${on}`,
  // Not drawn — "Left the board" with no date recorded.
  statusLeft: (on: string | null) => (on === null ? 'Left the board' : `Left the board ${on}`),
  // Drawn, o3-dashboard (the "Changes from 2026" column).
  seeChanges: 'See changes',
  noChanges: 'No changes',
  firstYear: 'First year',
  // Drawn, o3-dashboard (Priya Raman's row).
  send: 'Send',
  // Drawn, o3-dashboard (the rows' menus).
  seeAnswers: 'See answers',
  downloadOne: 'Download (PDF)',
  correctFromPaper: 'Correct from a paper form',
  remind: 'Remind',
  recordPaper: 'Record a paper form',
  printPaper: 'Print a paper form',
  // Not drawn — a row's menu's accessible name.
  actionsFor: (name: string) => `Actions for ${name}`,
  // Not drawn — a send went out to a joiner.
  sentTo: (name: string) => `Sent to ${name}.`,
  // Not drawn — the year's read failed.
  yearUnavailable: 'We couldn’t load this year’s conflicts of interest forms.',
  // Not drawn — the questions a paper form or a new year asks didn't load.
  questionsUnavailable: 'We couldn’t load the questions just now. Please try again.',
  tryAgain: 'Try again',
  // The register dialog's own words for a failed act (approved there), reused.
  couldNotSave: 'We couldn’t save that. Please try again.',
  // Not drawn — a PDF that didn't download.
  couldNotDownload: 'We couldn’t get that PDF just now. Please try again.',

  // ── Reminding (o4-remind) ───────────────────────────────────────────────────────────────────────

  // Drawn, o4-remind ("Remind 2 people"); one person not drawn.
  remindTitle: (names: readonly string[]) =>
    names.length === 1 ? `Remind ${names[0]}` : `Remind ${people(names.length)}`,
  // Drawn, o4-remind.
  remindLead: (label: string) =>
    `They haven’t filled out their ${label} form yet. Each gets one email.`,
  // Not drawn — one person.
  remindLeadOne: (label: string) =>
    `They haven’t filled out their ${label} form yet. They get one email.`,
  // Drawn, o4-remind.
  lastReminded: (on: string) => `Last reminded ${on}`,
  // Not drawn — someone sent the request but never reminded.
  sentOn: (on: string) => `Sent ${on}`,
  // Drawn, o4-remind.
  includesInvitation: 'The email includes an invitation to join Board.',
  // Drawn, o4-remind ("Send 2 reminders").
  sendReminders: (count: number) => (count === 1 ? 'Send the reminder' : `Send ${count} reminders`),
  // Not drawn — the reminders went out.
  remindersSent: (sent: number) => (sent === 1 ? '1 reminder sent.' : `${sent} reminders sent.`),

  // ── Sending to someone who joined (not drawn: the row's Send) ───────────────────────────────────

  // CBG-YR4-COI-JOINER (approved, CBG-YR4-COPY1's first part; not drawn): the members who joined
  // after a closed year closed, as their own group on its dashboard, with their Send.
  joinersGroup: 'Joined after these forms closed',
  joinersLine: (label: string) =>
    `They weren’t on the board for the ${label} conflicts of interest forms. You can send them the ${label} conflicts of interest form; their answers are recorded as given after joining.`,
  // A button, five words as before: the line beside it names the form.
  sendJoiners: (label: string) => `Send them the ${label} form`,
  sendJoinersTitle: (label: string) =>
    `Send the ${label} conflicts of interest form to everyone who joined after it closed?`,
  sendJoinersLead: (count: number) =>
    `${count === 1 ? 'One person gets' : `${count} people each get`} one email asking for their answers.`,
  sentToJoiners: 'Sent.',

  // Not drawn.
  sendTitle: (name: string) => `Send ${name} the conflicts of interest form`,
  sendLead: (label: string) =>
    `They joined the board after the ${label} conflicts of interest forms started. They get one email asking for their answers.`,
  // CBG-YR4-COI-JOINER (approved, CBG-YR4-COPY1's first part).
  sendLeadJoinedAfterClose: (label: string) =>
    `They joined the board after the ${label} conflicts of interest forms closed. They get one email asking for their answers, with no due date; their answers are recorded as given after joining.`,
  sendButton: 'Send',

  // ── Closing (o5-close) ──────────────────────────────────────────────────────────────────────────

  // Drawn, o5-close.
  closeTitle: (label: string) => `Close the ${label} forms?`,
  closeCount: (submitted: number, total: number, outstanding: readonly string[]) =>
    outstanding.length === 0
      ? `${submitted} of ${total} have filled it out.`
      : `${submitted} of ${total} have filled it out. Not yet: ${nameList(outstanding)}.`,
  closeRecord:
    'Closing adds a record to your minute book saying who was asked, and who filled it out and when. It names no one’s interests.',
  // The page's recommendation (CBG-YR2-COPY1's note): a closed year takes first answers only until
  // the next year's forms start (the backend refuses them after that).
  closeLate:
    'Anyone who hasn’t filled it out can still do so until the next year’s conflicts of interest forms start. Theirs are recorded as received after the year closed.',
  closeAndRecord: 'Close and record',
  // Not drawn — the close went through.
  closed: (label: string) =>
    `The ${label} conflicts of interest forms are closed, and the record is in your minute book.`,

  // ── The due date, and withdrawing (not drawn) ───────────────────────────────────────────────────

  // Not drawn.
  dueTitle: 'Change the due date',
  dueNote: 'Nothing is sent. People see the new date in Board, and in any reminder you send.',
  dueSave: 'Change the date',
  dueChanged: 'The due date is changed.',
  withdrawTitle: (label: string) => `Withdraw the ${label} forms?`,
  withdrawLead:
    'Nobody has answered yet. Withdrawing adds nothing to your minute book, and each person’s conflicts of interest form says it was withdrawn.',
  withdrawKeep: 'Keep them open',
  withdrawConfirm: 'Withdraw',
  withdrawn: (label: string) => `The ${label} conflicts of interest forms are withdrawn.`,

  // ── A paper form (not drawn: the derivation §2.14) ──────────────────────────────────────────────

  // Not drawn.
  paperTitle: (name: string) => `Record ${name}’s paper form`,
  paperCorrectTitle: (name: string) => `Correct ${name}’s answers from a paper form`,
  paperLead: 'Enter the answers as they’re written on the signed form.',
  paperCorrectLead:
    'Enter the answers as the signed paper correction gives them. The correction is added beside the answers already recorded; nothing recorded is changed.',
  paperCarried: (label: string) =>
    `Their answers from ${label} are filled in, as the printed form shows them. Change anything the signed form answers differently.`,
  paperSignedOn: 'Date signed on the form',
  paperNote: 'Where it came from',
  paperNoteHelp: 'Required. Say who gave you the signed form, and when.',
  paperScan: 'Where the signed form is kept (optional)',
  paperScanHelp: 'A pointer to your copy, for example a file name in your records.',
  paperRecord: 'Record the form',
  paperCorrect: 'Record the correction',
  paperRecorded: (name: string) => `${name}’s paper form is recorded.`,
  paperCorrected: (name: string) => `${name}’s correction is recorded.`,
  // Not drawn — a first answer the paper road won't take now.
  paperClosed: 'This year takes no new answers from this person now.',
  // Not drawn — the officer copies the person's statement as the form prints
  // it, named as theirs, and the tick records what the form shows (the review fold of cut yr2web;
  // the copy round's third question).
  paperStatementLead: (name: string) => `${name}’s statement, as printed on the form:`,
  paperTicked: 'Ticked on the signed form',

  // ── One person's answers (o6-answers, o7-changes) ───────────────────────────────────────────────

  // Drawn, o6-answers (the back link, "2027 forms", the year's heading).
  backToYear: (label: string) => `${label} forms`,
  // Drawn, o6-answers ("Director · conflicts of interest form, 2027 · the questions
  // asked about 2026").
  responseFacts: (capacity: string, label: string, period: string) =>
    `${capacity ? `${capacity} · ` : ''}conflicts of interest form, ${label} · the questions asked about ${period}`,
  // Drawn, o6-answers (the Download menu).
  download: 'Download',
  theseAnswers: 'These answers (PDF)',
  theseAnswersNote: 'A copy of what was signed',
  theseAnswersPaperNote: 'A copy of what was recorded',
  changesPdf: (label: string) => `Changes from the ${label} form (PDF)`,
  changesPdfNote: 'Both years, marked up',
  // Drawn, o6-answers and o7-changes (the two views).
  answersTab: 'Answers',
  changesTab: (label: string) => `Changes from the ${label} form`,
  // Drawn, o6-answers ("Signed by Theo Barrett in Board on Jan 14, 2027 at
  // 8:05 PM, confirmed with a passkey.").
  signedBy: (name: string, when: string, factor: string | null) =>
    `Signed by ${name} in Board on ${when}${factor ? `, confirmed with ${factor}` : ''}.`,
  // Not drawn — the derivation §2.5 ("Recorded by Maya Solberg from a paper form signed Jan 9").
  // Not drawn: "an officer", when the officer's name isn't shown.
  recordedFromPaper: (officer: string | null, signedOn: string) =>
    `Recorded by ${officer ?? 'an officer'} from a paper form signed ${signedOn}.`,
  // Not drawn — the paper road's provenance note, as the officer wrote it.
  paperNoteLine: (note: string) => `Where it came from: ${note}`,
  // Drawn, o6-answers.
  inMinuteBookNote:
    'In your minute book as this person’s form. This page shows the record’s own words.',
  // Not drawn — a response that a correction replaced, and one that corrects.
  correctedLater: 'These answers were corrected later.',
  seeCorrection: 'See the corrected answers',
  correctsEarlier: 'These answers correct the ones recorded before them.',
  // Not drawn — the backend's clean-copy line for a late answer (§20), as its version 3 says it.
  receivedAfterLabelClosed: (label: string) =>
    `Received after the ${label} conflicts of interest forms closed.`,
  // Not drawn — the backend's first-year line (§20), as its version 3 says it.
  firstInBoard: (name: string) =>
    `This is the first conflicts of interest form in Board for ${name}.`,
  // Drawn, o6-answers (the clean copy's parts).
  boardsPolicy: 'The board’s policy',
  policyAdopted: (title: string, on: string | null) =>
    on === null ? title : `${title}, adopted ${on}`,
  notTicked: 'Not ticked.',
  interests: 'Interests',
  anythingElse: 'Anything else',
  nothingAdded: 'Nothing added.',
  yes: 'Yes',
  no: 'No',
  // Drawn, o7-changes.
  changesHeader: (name: string, before: string, beforeOn: string, after: string, afterOn: string) =>
    `${name}’s ${before} answers (signed ${beforeOn}) beside the ${after} answers (signed ${afterOn}).`,
  changesNote:
    'Made from the two recorded disclosures; not itself a record. Added words are underlined, removed ones struck through.',
  changed: 'Changed',
  noChange: 'No change',
  answerLabel: 'Answer:',
  added: 'Added',
  removed: 'Removed',
  // Drawn, o7-changes ("3. Transactions with Harborlight: No, as in 2026").
  asIn: (title: string, answer: string, label: string) => `${title}: ${answer}, as in ${label}`,
  // Not drawn — a question asked this year only, and one asked last year only.
  newThisYear: 'Asked this year for the first time.',
  notAskedThisYear: 'Not asked this year.',
  // Drawn, o7-changes ("The statement about the policy: ticked again this
  // year."); the other three not drawn.
  statementAgain: 'The statement about the policy: ticked again this year.',
  statementNow: 'The statement about the policy: ticked this year, not last year.',
  statementNotNow: 'The statement about the policy: not ticked this year.',
  statementNeither: 'The statement about the policy: not ticked, as in the year before.',
  // Drawn, o7-changes.
  twoFingerprints: 'The two records, by their fingerprints',

  // ── The register, grouped by person (o8-register) ───────────────────────────────────────────────

  // Drawn, o8-register ("The 2027 conflicts of interest forms are open: 4 of 7
  // filled out, due Jan 29.").
  registerYearLine: (label: string, submitted: number, total: number, due: string) =>
    `The ${label} conflicts of interest forms are open: ${submitted} of ${total} filled out, due ${due}.`,
  seeWhosSubmitted: 'See who’s filled it out',
  // Drawn, o8-register ("Everyone"; v0 said "Every seat").
  everyone: 'Everyone',
  // Drawn, o8-register ("2027 answers").
  answersOf: (label: string) => `${label} answers`,
  // Drawn, o8-register ("Conflicts of interest form, 2027 · signed Jan 14, 2027").
  annualSigned: (label: string, on: string) =>
    `Conflicts of interest form, ${label} · signed ${on}`,
  annualFromPaper: (label: string, on: string) =>
    `Conflicts of interest form, ${label} · recorded from a paper form ${on}`,

  // ── A yearly form's record, on its page and in the book (CBG-UX-COPY3's D13) ───────────────────

  // Approved (D13, D-CBG-42 as amended 2026-10-29 08:36 PDT, "Form"): the record a person's yearly
  // form makes is their "form" wherever a person reads it, the kind on the wire staying
  // `disclosure`; a single disclosure keeps "Disclosure", "Disclosed by …" and "Disclosed to the
  // board". Its kind, as the record's own line and the book's Type column name it.
  formKind: 'Conflicts of interest form',
  // Its approval line: signed in Board, or recorded from the paper form the person signed. The
  // paper line takes the person's name alone, a possessive no phrase of titles can carry.
  formSignedBy: (person: string) => `Signed by ${person}`,
  formRecordedFromPaper: (name: string) => `Recorded from ${name}’s paper form`,
  // Approved as built (CBG-UX-COPY5, D-CBG-52: "yes keep as is", 2026-11-01 12:32 PST), no
  // frame: a paper form whose person's name isn't shown, the unnamed person after the noun as
  // copy N5 has it, never "…isn’t shown’s paper form".
  formRecordedFromPaperUnnamed: 'Recorded from the paper form of someone whose name isn’t shown',
  // Its making event's line.
  formMadeEvent: 'Signed and added to the minute book',
  // Drawn, o8-register.
  namesNoInterest: 'Names no interest.',
  newSince: (label: string) => `New since the ${label} form`,
  position: (position: string) => `Position: ${position}`,
  // CBG-UX-COPY3's D10: the answer to "Whose position" said as an answer, as the position is, so the
  // register, the person's own card and the answers pages say an entry alike.
  whose: (whose: string) => `Whose: ${whose}`,
  replaces: (label: string, on: string) => `Replaces the ${label} answers (${on}).`,
  disclosedOnItsOwn: (on: string) => `Disclosed outside the yearly form · ${on}`,
  notSubmittedYet: (label: string) => `${label} form: not filled out yet.`,
  // Not drawn — a person whose answers were replaced by a correction: a single disclosure keeps its
  // word, a yearly form's says "form" (CBG-UX-COPY3's D13).
  replacedByLater: 'Replaced by a later disclosure.',
  replacedByLaterForm: 'Replaced by a later form.',
  // Approved (CBG-YR2-WEB3's copy page, 2026-10-14), drawn y4: after an annual entry's date when
  // it was corrected since, so the words shown are the correction's (contract §10.1).
  corrected: 'corrected',
  // Each register entry's citations (CBG-YR2-WEB3, drawn y4): its record is `inMinuteBook`, above.
  // Approved (CBG-YR2-WEB3's copy page, 2026-10-14): the correction whose words an annual
  // entry shows.
  theCorrection: 'The correction',
  // v0's register's shipped words (`conflicts-page.tsx`), reused: the meeting a single disclosure
  // was made at.
  meetingItWasMadeAt: 'The meeting it was made at',

  // ── The minute book (the year's closing record) ─────────────────────────────────────────────────

  // The backend's kind label (contract §20; the export prints it "Conflicts of Interest Forms") and
  // its readable copy's state word.
  kindLabel: 'Conflicts of interest forms',
  // The backend's "Closed by {name} on {day}." (contract §6), dated here as every approval line is:
  // the instant in the reader's own time zone. Not drawn: a closer whose name isn't shown, set off
  // by a comma before "on" as every approval line's fallback is (CBG-UX3), so the day never reads
  // as part of it; the backend's own line says it the same way.
  closedBy: (name: string | null, when: string | null) =>
    name === null
      ? `Closed by someone whose name isn’t shown${when === null ? '' : `, on ${when}`}`
      : `Closed by ${name}${when === null ? '' : ` on ${when}`}`,
  // Not drawn — the close's own line in the record's event log, where a
  // disclosure's says "Disclosed to the board".
  closedEvent: 'Conflicts of interest forms closed',

  // ── The person's own year (WEB2) ────────────────────────────────────────────────────────────────

  // Not drawn in the frames; the derivation §5.2's row on the person's Board
  // home and the board's own pages ("Your 2027 conflicts of interest form · due Jan 29").
  attentionRow: (label: string, due: string) =>
    `Your ${label} conflicts of interest form · due ${due}`,
  // CBG-YR4-COI-JOINER (approved, CBG-YR4-COPY1's first part): a closed year sent to someone who
  // joined after it closed carries no due date — it was never theirs.
  attentionRowJoinedAfterClose: (label: string) => `Your ${label} conflicts of interest form`,
  waitingOnYou: 'Waiting on you',
  // Drawn, d1-year-card.
  yourYear: (label: string) => `Your ${label} conflicts of interest form`,
  // Approved (CBG-YR4-COPY1's next part, as this page's sibling of the self-assessment's), not
  // drawn: the page's title once its reads have settled with no year to name, never a loading bar
  // left for good; the approved title above without the year.
  yourAnnualDisclosure: 'Your conflicts of interest form',
  yearAsk: (organization: string, due: string) =>
    `${organization} asks everyone on its board to fill out a short conflicts of interest form once a year. Yours is due ${due}.`,
  // Not drawn — after the due date (the derivation §2.9's words).
  yearAskWasDue: (organization: string, due: string) =>
    `${organization} asks everyone on its board to fill out a short conflicts of interest form once a year. It was due ${due}. You can still fill it out.`,
  // CBG-YR4-COI-JOINER (approved, CBG-YR4-COPY1's next part; review-172 F1): the year card of
  // someone who joined after the year closed, with no due date — it was never theirs — in the
  // request email's own words.
  yearAskJoinedAfterClose: (organization: string, label: string) =>
    `${organization} asks everyone on its board to fill out a short conflicts of interest form once a year. Its ${label} forms were collected before you joined, so it’s asking you for yours now. There’s no due date.`,
  // Drawn, d1-year-card.
  filledIn: 'Your answers from last year are filled in.',
  start: 'Start',
  // Not drawn — answers kept on this device, not yet signed.
  continue: 'Continue',
  // Not drawn — the derivation §5.2's signed card ("Signed Jan 12 · Your copy ·
  // Changes from 2026").
  signedOn: (on: string) => `Signed ${on}`,
  recordedOn: (on: string) => `Recorded ${on}, from your paper form`,
  yourCopy: 'Your copy (PDF)',
  // Not drawn — a year withdrawn, and a year that takes no answer now.
  yearWithdrawn: 'This year’s conflicts of interest forms were withdrawn.',
  yearClosedNoAnswer: 'This year’s conflicts of interest forms are closed.',
  // Drawn, d1-year-card.
  inBoardsRecords: 'In the board’s records',
  annualOwnTitle: (label: string) => `Conflicts of interest form, ${label}`,
  yourAnswers: 'Your answers',
  // Drawn, d2-fresh and d3b-rolled-opens (the page's title and its line). CBG-FIX143's house form
  // here and in the four lines about the board's records below (CBG-UX-COPY2's fold, F2).
  forBoard: (organization: string, due: string, period: string) =>
    `For the ${houseFormName(organization)} board. Due ${due}. The questions are about ${period}.`,
  // CBG-YR4-COI-JOINER (approved, CBG-YR4-COPY1's next part; review-172 F1): the same line for
  // someone who joined after the year closed, without the date that was never theirs.
  forBoardJoinedAfterClose: (organization: string) =>
    `For the ${houseFormName(organization)} board.`,
  // Drawn, d2-fresh.
  freshIntro: (organization: string) =>
    `${organization} asks everyone on its board to answer these once a year. It takes a few minutes.`,
  // Drawn, d3b-rolled-opens.
  rolledIntro:
    'Your answers from last year are filled in. Change anything that’s different this year, then sign.',
  // Not drawn — a correction of answers already signed.
  correctingIntro:
    'Your signed answers are filled in. Change what was wrong, then sign. The correction is added beside them; nothing you signed is changed.',
  // Drawn, d2-fresh and d3b-rolled-opens.
  becomeDisclosure: 'When you sign, your form goes into the board’s records.',
  // Behind "How this works" (the mockups' word table).
  directorHow: [
    'Your signed form goes into the board’s minute book, in place of last year’s.',
    'Answering decides nothing. Whether anyone takes part in a particular vote is the board’s own decision, and the board records that in its minutes.',
    'Your officers can see your answers. Other members of the board see only their own.',
  ],
  // Not drawn — the derivation §2.6's context, never merged.
  alsoTold: 'You’ve also told the board, since your last conflicts of interest form:',
  // Drawn, d2-fresh.
  readIt: 'Read it',
  adopted: (on: string) => `Adopted ${on}`,
  // Drawn, d3b-rolled-opens ("Not carried over from last year: you tick it for
  // 2027.").
  notCarried: (label: string) => `Not carried over from last year: you tick it for ${label}.`,
  // Drawn, d2-fresh ("Your interests, and your family’s, during 2026").
  interestsDuring: (period: string) => `Your interests, and your family’s, during ${period}`,
  // Drawn, d2-fresh (the "What counts" link's own words).
  whatCounts: 'What counts',
  // Drawn, d2-fresh and d3b-rolled-opens.
  edit: 'Edit',
  remove: 'Remove',
  addAnother: 'Add another',
  // Drawn, d2-fresh.
  detailsOptional: 'Details (optional)',
  optional: 'Optional',
  // Drawn, d3-rolled.
  lastYear: (answer: string) => `Last year: ${answer}`,
  undo: 'Undo',
  anythingToAdd: 'Anything to add about this answer? (optional)',
  // Drawn, d2-fresh and d3b-rolled-opens.
  reviewAndSign: 'Review and sign',
  // Not drawn — what the review asks for before signing.
  answerEach: 'Answer each question, then review and sign.',
  nameEach: (field: string) => `Fill in “${field}” for each one, or remove it.`,
  // Drawn, d4-review.
  checkYourAnswers: 'Check your answers',
  // Drawn, d4-review ("You changed 2 answers from last year. The rest are as you answered them in
  // 2026, and you’ve ticked the statement about the board’s policy."); its other arms not drawn.
  reviewSummary: (changed: number, label: string, statement: 'ticked' | 'unticked' | 'none') => {
    const changes =
      changed === 0
        ? `Your answers are as you answered them in ${label}`
        : `You changed ${changed === 1 ? '1 answer' : `${changed} answers`} from last year. The rest are as you answered them in ${label}`;
    if (statement === 'ticked')
      return `${changes}, and you’ve ticked the statement about the board’s policy.`;
    if (statement === 'unticked')
      return `${changes}. You haven’t ticked the statement about the board’s policy; your answers record that.`;
    return `${changes}.`;
  },
  // Not drawn — a first year's review.
  reviewFirst: (statement: 'ticked' | 'unticked' | 'none') =>
    statement === 'unticked'
      ? 'Your answers are below. You haven’t ticked the statement about the board’s policy; your answers record that.'
      : 'Your answers are below.',
  seeAllAnswers: 'See all your answers',
  // Drawn, d4-review.
  signingAsks: (organization: string, label: string) =>
    `Signing asks you to confirm it’s you. Your ${label} form then goes into the ${houseFormName(organization)} board’s records.`,
  // Not drawn — the same line for a correction.
  signingAsksCorrection: (organization: string, label: string) =>
    `Signing asks you to confirm it’s you. Your corrected form then goes into the ${houseFormName(organization)} board’s records beside your ${label} form.`,
  // Copy round 2's line for a changed sentence (D-CBG-18; the consents page's `WORDS_CHANGED.sign`).
  wordsChanged: 'The words above changed. Read them, then sign again.',
  backToQuestions: 'Back to the questions',
  sign: 'Sign',
  // Drawn, d5-signed.
  signed: 'Signed',
  // Not drawn — the same card for answers an officer entered from paper.
  recorded: 'Recorded',
  signedLine: (label: string, organization: string, when: string, factor: string | null) =>
    `Your ${label} form is in the ${houseFormName(organization)} board’s records. You signed it on ${when}${
      factor ? `, confirmed with ${factor}` : ''
    }.`,
  // Not drawn — answers an officer entered from the person's paper form.
  recordedLine: (label: string, organization: string, on: string) =>
    `Your ${label} form is in the ${houseFormName(organization)} board’s records, recorded from your paper form signed ${on}.`,
  somethingWrong: 'Something wrong in what you sent?',
  correctYourAnswers: 'Correct your answers',
  correctionNote: 'A correction is added beside these; nothing you signed is changed.',
  // Not drawn — the person's own changes, opened from the signed card.
  yourChanges: (label: string) => `Changes from the ${label} form`,
  // Not drawn — the person's conflicts of interest form could not be read.
  questionnaireUnavailable: 'We couldn’t load your conflicts of interest form just now.',
} as const;

/** "a passkey" / "an authenticator app": the factor a signature was confirmed with (§20's words). */
export function factorWords(method: string | null | undefined): string | null {
  if (method === 'passkey_uv') return 'a passkey';
  if (method === 'totp') return 'an authenticator app';
  return null;
}

/** "your passkey" / "your authenticator app": the same, in the person's own copy (d5-signed). */
export function ownFactorWords(method: string | null | undefined): string | null {
  if (method === 'passkey_uv') return 'your passkey';
  if (method === 'totp') return 'your authenticator app';
  return null;
}
